import {
   getDateFromDateObject,
   sanitizeFileName
} from '@flowwai-frontend/shared/utils/common'
import { SERVER_DATE_TIME_FORMAT } from '@flowwai-frontend/shared/constants'

import {
   UnionFieldResponse,
   UnionFieldResponseType,
   UnionFieldType,
   UnionFieldResponseModel
} from '../types/Field'
import AttachmentModel from '../models/AttachmentModel'
import { RelatedRecord } from '../types/Field/relationField'
import {
   FieldVerificationType,
   PhoneNumberResponse
} from '../types/Field/field'
import { GeoShapeFieldResponseType } from '../types/Field/geoLocationField'
import { validatePhoneNumberField } from './fieldValidations'

export const isFieldResponseAvailable = (
   fieldResponse: UnionFieldResponse
): boolean => {
   switch (fieldResponse.fieldType) {
      case 'CHECKBOX_GROUP': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'DATE_TIME':
      case 'TIME':
      case 'DATE': {
         return fieldResponse.response ? true : false
      }

      case 'DROPDOWN': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'EMAIL': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'FIELDS_SELECTOR': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'FILE_UPLOADER': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'FLOAT': {
         const response = fieldResponse.response
         return response && response.toString().trim() ? true : false
      }

      case 'GOF_SELECTOR': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'IMAGE_UPLOADER': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'INTEGER': {
         const response = fieldResponse.response
         return response && response.toString().trim() ? true : false
      }

      case 'LONG_TEXT': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'MULTI_SEARCHABLE': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'MULTI_SELECT': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'PHONE_NUMBER': {
         const response = fieldResponse.response
         return response?.dialCode &&
            response?.iso2CountryCode &&
            response?.phoneNumber
            ? true
            : false
      }

      case 'PLAIN_TEXT': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'SHORT_TEXT': {
         const response = fieldResponse.response
         return response?.value.trim() ? true : false
      }

      case 'RADIO_GROUP': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'SEARCHABLE': {
         const response = fieldResponse.response
         return response ? true : false
      }

      case 'URL': {
         const response = fieldResponse.response
         return response && response.trim() ? true : false
      }

      case 'RELATION': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      case 'GEO_LOCATION': {
         const response = fieldResponse.response
         return response ? true : false
      }
      case 'GEO_SHAPE': {
         const response = fieldResponse.response
         return response && response.length ? true : false
      }

      default:
         return false
   }
}

//FIXME: resolve type issues
export const fieldResponseRequestObj = (
   fieldType: UnionFieldType,
   response: UnionFieldResponseType
): string | null => {
   switch (fieldType) {
      case 'CHECKBOX_GROUP':
         return response ? JSON.stringify(response) : null
      case 'DATE': {
         return response ? getDateFromDateObject(response as Date) : null
      }
      case 'DATE_TIME': {
         return response
            ? getDateFromDateObject(response as Date, SERVER_DATE_TIME_FORMAT)
            : null
      }
      case 'DROPDOWN':
         return response ? (response as string) : null
      case 'EMAIL':
         return response ? (response as string) : null
      case 'FIELDS_SELECTOR':
         return response ? (response as string) : null
      case 'FILE_UPLOADER': {
         if (response) {
            const attachmentsData = (response as AttachmentModel[]).map(
               (attachment: AttachmentModel) => ({
                  file_id:
                     attachment.attachmentId === '-1'
                        ? undefined
                        : attachment.attachmentId,
                  name: sanitizeFileName(attachment.name),
                  mime_type: attachment.mimeType,
                  size_in_bytes: attachment.sizeInBytes,
                  url: attachment.url
               })
            )
            return JSON.stringify(attachmentsData)
         }

         return null
      }
      case 'RELATION': {
         if (response) {
            const recordIds = (response as RelatedRecord[]).map(
               record => record.pipelineItemId
            )
            return JSON.stringify(recordIds)
         }
         return null
      }
      case 'FLOAT':
         return response ? (response as string) : null
      case 'GOF_SELECTOR':
         return response ? (response as string) : null
      case 'IMAGE_UPLOADER':
         return response ? (response as string) : null
      case 'INTEGER':
         return response ? (response as string) : null
      case 'LONG_TEXT':
         return response ? (response as string) : null
      case 'MULTI_SEARCHABLE':
         //TODO: need to check and update
         return response ? JSON.stringify(response) : null
      case 'MULTI_SELECT':
         return response ? JSON.stringify(response) : null
      case 'PHONE_NUMBER': {
         const phoneNumberResponse = response as PhoneNumberResponse

         const isPhoneNumberNotAvailable =
            phoneNumberResponse.phoneNumber.trim() === ''

         if (isPhoneNumberNotAvailable) return null

         const requestObj = {
            phone_number: phoneNumberResponse.phoneNumber,
            dial_code: phoneNumberResponse.dialCode,
            iso2_country_code: phoneNumberResponse.iso2CountryCode
         }
         return JSON.stringify(requestObj)
      }
      case 'PLAIN_TEXT':
         return response ? (response as string) : null
      case 'SHORT_TEXT':
         return response ? JSON.stringify(response) : null
      case 'RADIO_GROUP':
         return response ? (response as string) : null
      case 'SEARCHABLE':
         //TODO: need to check and update
         return response ? (response as string) : null
      case 'URL':
         return response ? (response as string) : null
      case 'TIME':
         return response ? (response as string) : null //TODO: need to update when we add time field
      case 'GEO_LOCATION':
         return response ? JSON.stringify(response) : null
      case 'GEO_SHAPE': {
         const geoShapeResponse = response as GeoShapeFieldResponseType
         if (!geoShapeResponse || !geoShapeResponse.length) return null

         return JSON.stringify(geoShapeResponse)
      }

      default:
         return null
   }
}
export const getIsFieldResponseUpdated = (
   originalFieldResponse: UnionFieldResponse,
   updatedFieldResponse: UnionFieldResponse
): boolean => {
   switch (originalFieldResponse.fieldType) {
      case 'CHECKBOX_GROUP':
      case 'MULTI_SEARCHABLE':
      case 'MULTI_SELECT': {
         const stringifiedOriginalFieldResponse = originalFieldResponse.response
            ?.length
            ? JSON.stringify(originalFieldResponse.response?.slice().sort())
            : ''

         const stringifiedUpdatedFieldResponse = (updatedFieldResponse.response as Array<
            any
         >)?.length
            ? JSON.stringify(
                 (updatedFieldResponse.response as Array<any>)?.slice().sort()
              )
            : ''

         return (
            stringifiedOriginalFieldResponse !== stringifiedUpdatedFieldResponse
         )
      }

      default: {
         const stringifiedOriginalFieldResponse = JSON.stringify(
            originalFieldResponse.response
         )
         const stringifiedUpdatedFieldResponse = JSON.stringify(
            updatedFieldResponse.response
         )

         return (
            stringifiedOriginalFieldResponse !== stringifiedUpdatedFieldResponse
         )
      }
   }
}

export const getUnionFieldResponseTypeFromUnionFieldResponseModel = (
   fieldResponseModel: UnionFieldResponseModel,
   isDirtyResponse = false
): UnionFieldResponse => ({
   fieldResponseId: fieldResponseModel.fieldResponseId,
   fieldId: fieldResponseModel.field.fieldId,
   fieldType: fieldResponseModel.fieldType,
   response: isDirtyResponse
      ? (fieldResponseModel.dirtyResponse as any)
      : (fieldResponseModel?.response as any)
})

export const getIsFieldResponseAvailableFromFieldId = (
   fieldId: string,
   getFieldResponseModel: (fieldId: string) => UnionFieldResponseModel
): boolean => {
   try {
      const fieldResponseModel = getFieldResponseModel(fieldId)

      return isFieldResponseAvailable(
         getUnionFieldResponseTypeFromUnionFieldResponseModel(
            fieldResponseModel
         )
      )
   } catch {
      return false
   }
}

export const getFieldVerificationStatusFromResponse = (
   response: UnionFieldResponseType,
   fieldType: UnionFieldType
): FieldVerificationType | null => {
   switch (fieldType) {
      case 'PHONE_NUMBER': {
         const phoneNumberResponse = response as PhoneNumberResponse
         return phoneNumberResponse?.otpVerificationStatus ?? null
      }
      default: {
         return null
      }
   }
}

export const getIsValidFieldResponse = (
   response: UnionFieldResponse,
   fieldType: UnionFieldType
): boolean => {
   const hasFieldResponse = isFieldResponseAvailable(response)

   if (!hasFieldResponse) return false

   switch (fieldType) {
      case 'PHONE_NUMBER': {
         return validatePhoneNumberField(
            response.response as PhoneNumberResponse
         ).isValid
      }
      default: {
         return false
      }
   }
}
